import { useTranslation } from "react-i18next";
import { SectionTitle } from "../../../../../../components/Form/Form";
import styled from "styled-components";
import { RegularTextSmall } from "../../../../../../components/Typography/Typography";
import { IDMobile } from "../../../../../../components/IDs/IDs";
import type {
  UploadAssetsResponseSchema,
  UploadedAssetInProgressSchema,
} from "../../../../../../types/types.PIM";
import { Table } from "../../../../../../components/Table/Table";
import type { Dispatch, FormEvent, SetStateAction } from "react";
import { useCallback } from "react";
import { useEffect, useMemo, useState } from "react";
import { TablePlaceholder, useStoreState } from "../../../../../../util/util";
import { formatBytes } from "../util/AssetsUtil";
import { IconGroup } from "../../../../../../components/DocumentCard/DocumentCard";
import {
  DeleteButton,
  EditButton,
  InvisibleButton,
  PrimaryButtonLargeV2,
} from "../../../../../../components/Buttons/Buttons";
import {
  LoadingIcon,
  WarningIcon,
} from "../../../../../../components/Icons/Icons";
import ReactTooltip from "react-tooltip";
import type { AxiosError } from "axios";
import axios from "axios";
import { endpoints } from "../../../../../../endpoints";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import {
  ActiveCheckIcon,
  DestructiveXIcon,
} from "../../components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import type { CellContext, Getter } from "@tanstack/react-table";
import { spacingTokens } from "../../../../../../designTokens";

const TableContainer = styled.div`
  align-self: stretch;
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  border: solid ${({ theme }) => theme.resolve("border_md")}
    ${({ theme }) => theme.colors.secondaryLightGray};
  max-height: 70vh;
  min-height: 250px;
  div[class*="Table__TableWrapper"] {
    position: relative;
    th {
      position: sticky;
      top: -1px;
    }
  }
`;

const Input = styled.input.attrs({})`
  height: 32px;
  width: 100%;
  min-width: 100px;
  border: ${({ theme }) => `1 px solid ${theme.tertiaryBorder}`};
  padding: ${({ theme }) => theme.resolve("spacing_xs")} 0;
  outline: none;
  margin: 0;
  &:focus {
    border-radius: ${({ theme }) => theme.resolve("radius_xs")};
    border: ${({ theme }) =>
      `${theme.resolve("border_2xl")} solid ${theme.tertiaryBorder}`};
  }
  :focus:invalid {
    border: ${({ theme }) => theme.resolve("border_2xl")} solid
      ${({ theme }) => theme.errorColor};
  }
  :invalid {
    border: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme }) => theme.errorColor};
  }
`;

type TableData = UploadedAssetInProgressSchema & { edit: boolean };

export const ProcessUploadedAssets = ({
  onComplete,
  uploadUUID,
  uploadNumber,
  assets,
  existingAssets,
}: {
  onComplete: () => void;
  uploadUUID: string;
  uploadNumber: string;
  assets: UploadedAssetInProgressSchema[];
  existingAssets?: UploadedAssetInProgressSchema[];
}) => {
  const { tenant_id } = useStoreState();
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotifications();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [new_assets_errors, set_new_assets_errors] = useState(false);
  const [existing_assets_errors, set_existing_assets_errors] = useState(false);

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      // submit
      await axios.post(
        endpoints.v2_tenants_tenant_id_pim_assets_uploads_upload_id_submit(
          tenant_id,
          uploadUUID
        )
      );
      notifySuccess(`${uploadNumber} ${t("successfully uploaded")}`);
      setIsSubmitting(false);
      onComplete();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error uploading your files."),
        {
          error,
        }
      );
      setIsSubmitting(false);
    }
  };

  const hasError = useMemo(
    () =>
      new_assets_errors ||
      existing_assets_errors ||
      [...assets, ...(existingAssets ?? [])].length === 0,
    [assets, existingAssets, existing_assets_errors, new_assets_errors]
  );

  return (
    <div style={{ marginTop: spacingTokens.spacing_3xl }}>
      <SectionTitle style={{ margin: `0 0 ${spacingTokens.spacing_xl}` }}>
        {t("Assets")}
      </SectionTitle>
      <TableContainer>
        <IDMobile>{uploadNumber}</IDMobile>
        <AssetsTable
          assets={assets}
          uploadUUID={uploadUUID}
          setHasError={set_new_assets_errors}
        />
        {(existingAssets?.length ?? 0) > 0 && (
          <AssetsTable
            assets={existingAssets ?? []}
            uploadUUID={uploadUUID}
            setHasError={set_existing_assets_errors}
            asset_type="existing"
          />
        )}
      </TableContainer>
      <PrimaryButtonLargeV2
        style={{
          marginTop: "37px",
          marginBottom: spacingTokens.spacing_2xl,
          maxWidth: "512px",
        }}
        loading={isSubmitting}
        disabled={hasError}
        onClick={onSubmit}
        fullWidth
      >
        {t("Save")}
      </PrimaryButtonLargeV2>
    </div>
  );
};

const EditAssetName = ({
  text,
  name,
  onSubmit,
}: {
  text: string;
  name: string;
  onSubmit: (value: string) => void;
}) => {
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    const form = e.currentTarget;
    const formData = new FormData(form);
    onSubmit((formData.get(name) as String).trim());
    e.preventDefault();
  };
  return (
    <form onSubmit={handleSubmit} id={`form-${name}`}>
      <Input
        type="text"
        id={name}
        name={name}
        autoComplete="edit_asset_name"
        required
        defaultValue={text}
      />
    </form>
  );
};

const AssetsTable = ({
  assets,
  uploadUUID,
  setHasError,
  asset_type = "new",
}: {
  assets: UploadedAssetInProgressSchema[];
  uploadUUID: string;
  setHasError: Dispatch<SetStateAction<boolean>>;
  asset_type?: "new" | "existing";
}) => {
  const [tableData, setTableData] = useState<TableData[]>(
    assets.map((asset) => ({ ...asset, edit: false }))
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string>();
  const [deletingId, setDeletingId] = useState<string>();

  const [upload_count, set_upload_count] = useState<number>(assets.length);

  const { notifyError } = useNotifications();
  const { tenant_id } = useStoreState();
  const { t } = useTranslation();

  const exitEditMode = (id: string) => {
    setIsEditing(false);
    setTableData((prev) =>
      prev.map((data) => (data.id === id ? { ...data, edit: false } : data))
    );
  };

  const enterEditMode = (id: string) => {
    setIsEditing(true);
    setTableData((prev) =>
      prev.map((data) => (data.id === id ? { ...data, edit: true } : data))
    );
  };

  const putUpdate = useCallback(
    (updatedTableData: TableData[]) => {
      const update = async () => {
        const requestBody = {
          data: updatedTableData.map(({ asset_name, id }) => ({
            id,
            asset_name,
          })),
        };
        try {
          const {
            data: { data },
          } = await axios.put<UploadAssetsResponseSchema>(
            endpoints.v2_tenants_tenant_id_pim_assets_uploads_upload_id(
              tenant_id,
              uploadUUID
            ),
            requestBody
          );
          setTableData(data.map((asset) => ({ ...asset, edit: false })));
          setIsEditing(false);
        } catch (error) {
          const errorMessage = (error as AxiosError)?.response?.data?.message;
          notifyError(
            errorMessage
              ? errorMessage
              : t("Could not update uploaded assets. Please try again later."),
            {
              error,
            }
          );
        } finally {
          setDeletingId(undefined);
          setEditingId(undefined);
        }
      };
      update();
    },
    [notifyError, t, tenant_id, uploadUUID]
  );

  const tableColumns = useMemo(() => {
    const deleteAsset = (id: string, existingData: TableData[]) => {
      setDeletingId(id);
      setIsEditing(true);
      set_upload_count((prev) => prev - 1);
      const updatedData = existingData.filter((data) => data.id !== id);
      putUpdate(updatedData);
    };

    const updateTableData = ({
      id,
      text,
      existingData,
    }: {
      id: string;
      text: string;
      existingData: TableData[];
    }) => {
      setEditingId(id);
      const updatedTableData = existingData.map((data) =>
        data.id === id ? { ...data, asset_name: text } : data
      );
      putUpdate(updatedTableData);
    };

    const onEditSubmit = (id: string, text: string) => {
      updateTableData({
        id,
        text,
        existingData: tableData,
      });
    };

    return [
      {
        header: t("File Name"),
        accessorKey: "file_name",
        cell: (
          cell: CellContext<
            {
              id: string;
              text: string;
              existingData: TableData[];
            },
            unknown
          >
        ) => cell.renderValue(),
      },
      {
        header: t("Asset Name"),
        accessorKey: "asset_name",
        cell: ({
          getValue,
          row: { original },
        }: {
          getValue: Getter<string>;
          row: {
            original: UploadedAssetInProgressSchema & { edit: boolean };
          };
        }) => {
          return original.edit ? (
            <EditAssetName
              name={original.id}
              text={original.asset_name}
              onSubmit={(textVal) => onEditSubmit(original.id, textVal)}
            />
          ) : (
            <span>{getValue()}</span>
          );
        },
      },
      {
        header: t("Size"),
        accessorKey: "size",
        cell: ({ getValue }: { getValue: Getter<number> }) => (
          <span>{formatBytes(getValue())}</span>
        ),
      },
      {
        header: "",
        accessorKey: " ",
        cell: ({
          row: { original },
        }: {
          row: {
            original: TableData;
          };
        }) => {
          return original.edit ? (
            editingId === original.id ? (
              <LoadingIcon width={20} height={20} />
            ) : (
              <IconGroup style={{ justifyContent: "flex-end" }}>
                <InvisibleButton form={`form-${original.id}`} type="submit">
                  <ActiveCheckIcon />
                </InvisibleButton>
                <InvisibleButton onClick={() => exitEditMode(original.id)}>
                  <DestructiveXIcon />
                </InvisibleButton>
              </IconGroup>
            )
          ) : (
            <IconGroup style={{ justifyContent: "flex-end" }}>
              {original.errors ? (
                <>
                  <span
                    data-tip={original.errors ? original.errors : ""}
                    data-for={`${original.id}_error_tooltip`}
                    style={{ width: "43px", textAlign: "center" }}
                  >
                    <WarningIcon width={20} height={20} />
                  </span>
                  <ReactTooltip
                    id={`${original.id}_error_tooltip`}
                    type="dark"
                  />
                </>
              ) : (
                <EditButton
                  disabled={isEditing}
                  onClick={() => enterEditMode(original.id)}
                />
              )}
              {deletingId === original.id ? (
                <LoadingIcon width={20} height={20} />
              ) : (
                <DeleteButton
                  disabled={isEditing}
                  onClick={() => deleteAsset(original.id, tableData)}
                />
              )}
            </IconGroup>
          );
        },
      },
    ];
  }, [deletingId, editingId, isEditing, putUpdate, t, tableData]);

  const tableCaption = () => {
    const successfulUploads = tableData.filter((asset) => !asset.errors).length;
    const errorCount = tableData.length - successfulUploads;
    return tableData.length ? (
      <>
        {tableData.length !== upload_count && (
          <>
            <LoadingIcon width={22} height={22} />
          </>
        )}
        <span
          style={{
            padding: `0 ${spacingTokens.spacing_lg} 0 ${spacingTokens.spacing_md}`,
          }}
        >
          {successfulUploads} / {upload_count || tableData.length}{" "}
          {asset_type === "new"
            ? t("assets uploaded")
            : t("already exists with the same asset name")}
        </span>
        {errorCount > 0 && (
          <>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "arrow",
              }}
            >
              (<WarningIcon width={26} height={18} />{" "}
              {`${errorCount} error${errorCount > 1 ? "s" : ""} found `})
            </span>
          </>
        )}
      </>
    ) : (
      ""
    );
  };

  useEffect(() => {
    if (assets.length > 0) {
      setTableData(assets.map((asset) => ({ ...asset, edit: false })));
      set_upload_count(assets.length);
      setHasError(assets.some((data) => Boolean(data.errors)));
    }
  }, [assets, setHasError]);

  return (
    <>
      <RegularTextSmall style={{ display: "flex", alignItems: "center" }}>
        {tableCaption()}
      </RegularTextSmall>
      <Table
        columns={tableColumns}
        data={tableData}
        isLoading={false}
        error={undefined}
        Placeholder={<TablePlaceholder message={t("No items to show.")} />}
      />
    </>
  );
};
